import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { FiMail, FiUser, FiCheckCircle } from "react-icons/fi";
import { useAuth } from "../../authentication/AuthContext";
import "./AccountSetupCard.css";

// The dashboard's "finish setting up" checklist. Signup lets people in with only
// a name, email and password — email confirmation and the organization details
// are both deferred — so this is where the two loose ends get chased.
//
// It renders in the normal document flow at the top of the dashboard content,
// which is the fix for the previous floating banner: an element that occupies
// its own space cannot cover the page underneath it.
//
// Returns null when there is nothing outstanding, so a fully set-up account
// never sees it.
export default function AccountSetupCard() {
  const { status, verified, profileComplete, email } = useAuth();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);

  const needsVerify = status === "ready" && verified === false;
  const needsProfile = status === "ready" && profileComplete === false;

  if (!needsVerify && !needsProfile) return null;

  const resend = async () => {
    if (resending) return;
    setResending(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/ResendVerificationCode`, { email });
      if (res.data?.status === 1) {
        toast.success("Code sent — check your inbox.");
        navigate("/User/VerifyEmail");
      } else {
        toast.error(res.data?.message || "Could not send the code. Please try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to connect to the server. Please try again later.");
    } finally {
      setResending(false);
    }
  };

  const outstanding = (needsVerify ? 1 : 0) + (needsProfile ? 1 : 0);

  return (
    <div className="setup-card">
      <div className="setup-card-head">
        <span className="setup-card-badge" aria-hidden="true">
          <FiCheckCircle />
        </span>
        <div>
          <h3 className="setup-card-title">Finish setting up your account</h3>
          <p className="setup-card-sub">
            {outstanding === 1
              ? "One quick thing left."
              : `${outstanding} quick things left — they take about a minute.`}
          </p>
        </div>
      </div>

      <ul className="setup-tasks">
        {needsVerify && (
          <li className="setup-task">
            <span className="setup-task-icon" aria-hidden="true"><FiMail /></span>
            <span className="setup-task-body">
              <span className="setup-task-name">Confirm your email address</span>
              <span className="setup-task-hint">
                We sent a code to {email}. Confirming it secures your account and lets us reach you about your licenses.
              </span>
            </span>
            <span className="setup-task-actions">
              <button type="button" className="setup-btn" onClick={() => navigate("/User/VerifyEmail")}>
                Enter code
              </button>
              <button type="button" className="setup-btn is-quiet" onClick={resend} disabled={resending}>
                {resending ? "Sending…" : "Resend"}
              </button>
            </span>
          </li>
        )}

        {needsProfile && (
          <li className="setup-task">
            <span className="setup-task-icon" aria-hidden="true"><FiUser /></span>
            <span className="setup-task-body">
              <span className="setup-task-name">Complete your profile</span>
              <span className="setup-task-hint">
                Add your organization, country and phone number so we can set up licenses and quotes for you.
              </span>
            </span>
            <span className="setup-task-actions">
              <button type="button" className="setup-btn" onClick={() => navigate("/Profile")}>
                Add details
              </button>
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}
